import { curveMonotoneX, line } from 'd3-shape';
import { format } from 'date-fns';
import { toTruncated, toPercentage } from '@/utilities/formatting';
import { GROUP_TYPES, NODE_NAMES, tooltipMapping } from '../const';
import type { TimeSeriesItem } from '../../../../../hooks/useCompanyTimeSeries';
import type { D3Link, D3Node } from '../types';
import type { SankeyLinkMinimal, SankeyNodeMinimal } from 'd3-sankey';
import { sankeyLinkHorizontal } from 'd3-sankey';

export const getTooltipContent = (node: D3Node) => {
  if (node.name in tooltipMapping) {
    return tooltipMapping[node.name];
  }

  // Escape for revenue segments
  if (node.groupType === GROUP_TYPES.INCOME) {
    return tooltipMapping.segment;
  }
  if (node.groupType === GROUP_TYPES.REVENUE_ELIMINATION) {
    return tooltipMapping[NODE_NAMES.REVENUE_ELIMINATION];
  }

  return undefined;
};

export const formatter = (
  value?: number | null,
  currencySymbol = '',
  significance = 2
) =>
  value === null || value === undefined
    ? 'n/a'
    : `${currencySymbol}${toTruncated(value, significance)}`;

const isSankeyLinkMinimal = (link): link is SankeyLinkMinimal<object, object> =>
  'source' in link && 'target' in link;

const isSankeyNodeMinimal = (node): node is SankeyNodeMinimal<object, object> =>
  'sourceLinks' in node && 'targetLinks' in node;

export const normalPath = sankeyLinkHorizontal()
  .source((d) => {
    if (isSankeyLinkMinimal(d)) {
      const { source } = d;
      if (isSankeyNodeMinimal(source)) {
        const { x0 = 0, x1 = 0 } = source;
        const { y0 = 0 } = d;
        return [x0 + (x1 - x0), y0];
      }
    }
    return [0, 0];
  })
  .target((d) => {
    if (isSankeyLinkMinimal(d)) {
      const { target } = d;
      if (isSankeyNodeMinimal(target)) {
        const { x0 = 0 } = target;
        const { y1 = 0 } = d;
        return [x0, y1];
      }
    }
    return [0, 0];
  });

export const sankeyLineGenerator = () => {
  const lineGenerator = line().curve(curveMonotoneX);
  return (link: D3Link) => {
    const thickness = Math.max(1, link.width);
    const halfThickness = thickness / 2;
    const linkLength = link.target.x0 - link.source.x1;
    const padLength = linkLength * 0.001;
    const dots: [number, number][] = [
      [link.source.x1, link.y0 - halfThickness],
      [link.source.x1 + padLength, link.y0 - halfThickness],
      [link.target.x0 - padLength, link.y1 - halfThickness],
      [link.target.x0, link.y1 - halfThickness],
      [link.target.x0, link.y1 + halfThickness],
      [link.target.x0 - padLength, link.y1 + halfThickness],
      [link.source.x1 + padLength, link.y0 + halfThickness],
      [link.source.x1, link.y0 + halfThickness],
      [link.source.x1, link.y0 - halfThickness],
    ];
    return lineGenerator(dots) + 'Z';
  };
};

export const getSankeyMaxDepth = (nodes: D3Node[]) => {
  return nodes.reduce((maxDepth, node) => {
    return Math.max(node.depth, maxDepth);
  }, 0);
};

export const getIsReverseNode = (node: D3Node) => {
  const { sourceLinks, targetLinks } = node;
  if (targetLinks.length === 0 && sourceLinks[0]?.target?.key === 'Expenses')
    return true;
  return false;
};

export const getTextAnchor = (node: D3Node, maxNodeDepth) => {
  const isReverseNode = getIsReverseNode(node);
  if (node.depth === 0 && !isReverseNode) return 'start';
  if (node.depth === maxNodeDepth) return 'end';
  return 'middle';
};

export const getTextXCoord = (node: D3Node, maxNodeDepth: number) => {
  const isReverseNode = getIsReverseNode(node);
  if (node.depth === 0 && !isReverseNode) return node.x0;
  if (node.depth === maxNodeDepth) return node.x1 - node.x0;
  return (node.x1 - node.x0) / 2;
};

export const getTotalLeftNodes = (nodes: D3Node[]) => {
  return nodes.reduce((count, node) => {
    const isReverseNode = getIsReverseNode(node);
    return node.depth === 0 && !isReverseNode ? count + 1 : count;
  }, 0);
};

export const getTooltipAttachedNode = (link: D3Link) => {
  const isIncomeLink = link.groupType === GROUP_TYPES.INCOME;
  const isIncomeLinkButNotElimination =
    isIncomeLink && link.target.name !== NODE_NAMES.REVENUE_ELIMINATION;

  const isExpenseLinkButNotProfit =
    link.source.name !== NODE_NAMES.GROSS_PROFIT &&
    link.target.name === NODE_NAMES.EXPENSES;

  const shouldTakeSourceNode =
    isIncomeLinkButNotElimination || isExpenseLinkButNotProfit;

  return shouldTakeSourceNode ? link.source : link.target;
};

export const getTooltipAttachedLink = (node: D3Node) => {
  const targetLinks = node.targetLinks;
  const sourceLinks = node.sourceLinks;
  const links = [...targetLinks, ...sourceLinks];
  const validTargetLinks = links.filter(
    (link) => getTooltipAttachedNode(link).name === node.name
  );
  return validTargetLinks[0];
};

const getNearbyNode = (node: D3Node, link: D3Link) => {
  const isSource = link.source.name === node.name;
  return isSource ? link.target : link.source;
};

export const calcTooltipValue = (node: D3Node) => {
  const link = getTooltipAttachedLink(node);
  if (!link) {
    return [];
  }

  const nearByNode = getNearbyNode(node, link);
  const value = link.value / nearByNode.value;

  return [
    {
      name: nearByNode?.name,
      value: toPercentage(value, 1),
    },
  ];
};

export const getTodayDate = () => format(new Date(), 'yyyy-MM-dd');

export const getEndDateByYear = (year: number) =>
  format(new Date(year, 11, 31), 'yyyy-MM-dd');

export const getYearByDate = (date: string) => {
  const year = new Date(date).getFullYear();
  return year;
};

const range = (min: number, max: number): number[] => {
  if (min > max) {
    throw new Error(
      'Minimum value must be less than or equal to the maximum value.'
    );
  }

  const result: number[] = [];

  for (let i = min; i <= max; i++) {
    result.push(i);
  }

  return result;
};

export const getYearArrayFromTimeseries = (
  timeseries?: TimeSeriesItem[] | null
) => {
  if (!timeseries) {
    const todayDate = getTodayDate();
    const thisYear = getYearByDate(todayDate);
    return range(thisYear - 10, thisYear);
  }

  const yearArray = timeseries.map((item) => getYearByDate(item.date));
  if (yearArray.length === 0) {
    return [];
  }

  const min = Math.min(...yearArray);
  const max = Math.max(...yearArray);
  return range(min, max);
};

export const calcRectHeight = (node: D3Node, realRevenueValue?: number) => {
  const defaultRectHeight = Math.max(1, node.y1 - node.y0);
  const nodeValue = node.value;
  const percentage = realRevenueValue ? realRevenueValue / nodeValue : 1;

  const isRevenueNode = node.name === NODE_NAMES.REVENUE;

  const rectHeight = isRevenueNode
    ? defaultRectHeight * percentage
    : defaultRectHeight;
  const negRectHeight = defaultRectHeight - rectHeight;

  return {
    rectHeight,
    negRectHeight,
  };
};

type Entries = Record<number, number>;

export const getMiniChartEntries = (timeSeries?: TimeSeriesItem[] | null) => {
  return timeSeries?.reduce((acc, entry) => {
    if (entry.revenue === null) return acc;
    const timestamp = new Date(entry.date).getTime();
    return { ...acc, [timestamp]: entry.revenue };
  }, {} as Entries);
};

export const getMiniChartKeys = (timeSeries?: TimeSeriesItem[] | null) => {
  return timeSeries?.map((item) => new Date(item.date).getTime());
};
